/**
 * Draws the marquee (see logoModel.ts): the name in fat gradient letters with a drop shadow, a
 * white shine sweeping across them now and then, and the frame of chasing bulbs round it.
 */

import { INDIGO, WHITE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import {
  BLOCK_H, BLOCK_W, bulbLit, dropOffset, glyphRuns, LETTER_W, logoRamp, logoWidth, marqueeBulbs, shineAt, shineSpan,
} from './logoModel';
import type { Rect } from './menuModel';
import { calmStep, isCalm } from './theme';

const SHADOW = 2;
/** An unlit bulb: a dull red, as glass with the filament off. */
const BULB_OFF = '#5a1414';

/**
 * Draw `text` with its top-left at (x, y). `dropMs` is the time since the letters began to fall
 * (null: already in place); `ms` drives the shine.
 */
export function drawLogo(screen: Screen, text: string, x: number, y: number, ms: number, dropMs: number | null): void {
  const letters = [...text];
  const n = letters.length;
  const offsets = letters.map((_, i) => (dropMs === null ? 0 : dropOffset(dropMs, i)));
  // Every shadow first, so no letter's shadow falls on its neighbour's face.
  letters.forEach((ch, i) => {
    const ly = y + (offsets[i] ?? 0) + SHADOW;
    for (const run of glyphRuns(ch)) {
      const rx = x + i * LETTER_W + run.col * BLOCK_W + SHADOW;
      screen.fill(rx, ly + run.row * BLOCK_H, run.len * BLOCK_W, BLOCK_H, INDIGO);
    }
  });
  const shine = isCalm() ? null : shineAt(ms, logoWidth(text));
  letters.forEach((ch, i) => {
    const dy = offsets[i] ?? 0;
    const colours = logoRamp(i, n);
    for (const run of glyphRuns(ch)) {
      const x0 = i * LETTER_W + run.col * BLOCK_W;
      const w = run.len * BLOCK_W;
      for (let k = 0; k < BLOCK_H; k++) {
        const py = run.row * BLOCK_H + k;
        screen.fill(x + x0, y + dy + py, w, 1, colours[py] ?? WHITE);
        // The shine waits for a letter to land.
        const lit = shine === null || dy !== 0 ? null : shineSpan(shine, py, x0, x0 + w);
        if (lit) screen.fill(x + lit[0], y + py, lit[1] - lit[0], 1, WHITE);
      }
    }
  });
}

/** The frame of bulbs round `r`, one in three lit and the light running clockwise. */
export function drawMarquee(screen: Screen, r: Rect, ms: number): void {
  const step = Math.floor(ms / calmStep(110));
  marqueeBulbs(r).forEach((b, i) => {
    screen.fill(b.x - 1, b.y - 1, 2, 2, bulbLit(i, step) ? YELLOW : BULB_OFF);
  });
}
